/**
 * Use this to ease between changes to a value.
 *
 * Note. Knockout re-evaluates filters every time the value changes, so we use
 * memoization to make sure the same "easedValue" object is returned when
 * appropriate, otherwise the object with easing in progress is just replaced
 * with a fresh, already up-to-date object, skipping the easing.
 *
 * Usage Examples:
 *
 *      {{ value | ease }}                      // Use default settings
 *      {{ value | ease:2000 }}                 // Specify the duration
 *      {{ value | ease:2000:'easeOutQuad' }}   // Specify the duration and easing function
 *
 *      {{ value | easeUp }}                    // Only ease for increasing numbers
 */
define('common/ko/filters/easeFilter',['jquery', 'knockout', 'underscore', 'jquery.easing', 'knockout.punches'], function ($, ko, _) {

    ko.filters.ease = _.partial(easeFilter, function() { return true; });

    ko.filters.easeUp = _.partial(easeFilter, function(currentValue, newValue) { return newValue > currentValue; });

    function easeFilter(shouldEase, wrappedValue, easingDuration, easingFunction) {

        var innerEasedValue = ko.observable( wrappedValue.peek() ),
            easedValue = ko.pureComputed(innerEasedValue),
            valueSubscription,
            memoKey;

        easingDuration = easingDuration || 1000;
        easingFunction = easingFunction || 'easeOutQuint';

        memoKey = '_easedValue_' + easingFunction + easingDuration;

        // If there is already an easedValue available for this wrappedValue then use it.
        if (wrappedValue[memoKey]) {
            return wrappedValue[memoKey];
        }

        // Activate the functionality when the easedValue pureComputed is awake
        easedValue.subscribe(function() {

            var initialTime,
                initialValue,
                deltaValue,
                animationFrameId;

            wrappedValue[memoKey] = easedValue;

            valueSubscription = wrappedValue.subscribe(function(endValue) {

                initialTime = (new Date()).getTime();
                initialValue = innerEasedValue();
                deltaValue = endValue - initialValue;

                if (!shouldEase(initialValue, endValue)) {

                    innerEasedValue(endValue);

                    window.cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;

                    return;
                }

                if (!animationFrameId) {
                    animationFrameId = window.requestAnimationFrame(easeValue);
                }

            });

            function easeValue() {

                var deltaTime = (new Date()).getTime() - initialTime,
                    completeness = Math.min(1, deltaTime / easingDuration),
                    easedCompleteness = $.easing[easingFunction](0, completeness, 0, 1, 1);

                innerEasedValue( initialValue + deltaValue * easedCompleteness );

                if (completeness === 1) {
                    window.cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                } else {
                    animationFrameId = window.requestAnimationFrame(easeValue);
                }

            }

        }, null, 'awake');

        // Clean up everything when the pureComputed is asleep
        easedValue.subscribe(function() {
            delete wrappedValue[memoKey];
            easedValue.dispose();
            valueSubscription.dispose();
        }, null, 'asleep');

        return easedValue;
    }

});

